import {AfterViewInit, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit} from '@angular/core';
import {RequestService} from "../../../../core/services/request/request.service";
import {UtilService} from "../../../../core/services/util/util.service";
import {CatalogQuestionModel} from "../../../../core/services/catalog-data/catalog-question.model";
import  * as mime from 'mime';
import {Observable, Subscription} from "rxjs";
import {InternationalizationService} from "../../../../core/services/internationalization/internationalization.service";

// WARN this component has diverged significantly from KnockoutFieldComponent
@Component({
  selector: 'sb-catalog-field',
  templateUrl: './catalog-field.component.html',
  styleUrls: ['./catalog-field.component.scss']
})
export class CatalogFieldComponent implements OnInit, OnDestroy, AfterViewInit {

  @Input()
  field: CatalogQuestionModel;

  @Input()
  events: Observable<any>;

  private eventsSubscription: Subscription;

  firstSettingOfChoice: boolean = true;

  firstSettingOfHtml: boolean = true;

  firstSettingOfSwitch: boolean = true;

  koFieldModel: any = null;

  changed: EventEmitter<CatalogQuestionModel> = new EventEmitter<CatalogQuestionModel>();

  // Note that an attachment field is a reference field with referenceTable of 'Attachment'
  private datatypeToCreatorFn: {
    [key: string]: (model: CatalogFieldComponent, field: CatalogQuestionModel) => any
  } = {
    choice: this.createChoiceField,
    text: this.createScalarField,
    html: this.createScalarField,
    boolean: this.createScalarField,
    acknowledgement: this.createScalarField,
    integer: this.createScalarField,
    string: this.createScalarField,
    date_time: this.createScalarField,
    date: this.createScalarField,
    decimal: this.createScalarField,
    reference: this.createReferenceField,
    many_to_many: this.createReferenceField,
    money: this.createScalarField,
  };

  // Note that the default renderer for reference is 'attachment' if the referenceTable is 'Attachment'
  private datatypeToDefaultRenderer: {
    [key: string]: string } = {
    choice: "choice",
    text: "textarea",
    html: "html",
    boolean: "checkbox",
    acknowledgement: "checkbox",
    integer: "textfield",
    string: "textfield",
    date_time: "datetime",
    date: "date",
    decimal: "textfield",
    reference: "onetoone",
    many_to_many: "onetomany",
    money: "accounting"
  };

  constructor(private requestService: RequestService,
              private hostElement: ElementRef,
              private utilService: UtilService,
              private i18n: InternationalizationService) {

  }

  ngOnInit() {
    if (this.events) {
      this.eventsSubscription = this.events.subscribe((data) => this.eventSubscription(data));
    }
  }

  ngOnDestroy() {
    this.eventsSubscription && this.eventsSubscription.unsubscribe();
  }

  private eventSubscription(data) {
    switch (data) {
      case "validate":
        if (this.koFieldModel != null && this.koFieldModel.validateField) {
          this.koFieldModel.validateField();
        }
        break;
      case "validationMessages":
        if (this.field.data.validationMessage) {
          this.koFieldModel.value.hasError(true);
          this.koFieldModel.value.validationMessage(this.field.data.validationMessage);
        } else {
          this.koFieldModel.value.hasError(false);
          this.koFieldModel.value.validationMessage(null);
        }
        break;
    }
  }

  async ngAfterViewInit() {

    const element = this.hostElement.nativeElement;

    const creatorFn = this.datatypeToCreatorFn[this.field.datatype];

    if (creatorFn) {
      const data = await creatorFn(this, this.field);

      // Add the form display type, as Knockout uses this info for styles
      data.formDisplayType = "form";

      const fieldTemplate = this.field?.rendererProperties?.['reversed'] == 'true' ? 'nuclear-field-template-reversed' : 'nuclear-field-template';

      (window as any).KO.cleanNode(element);
      (window as any).KO.renderTemplate(fieldTemplate, data, {}, element, "replaceNode");
      this.koFieldModel = data.field;
    } else {
      console.error(`No creator function for field of type ${this.field.datatype}`);
    }
  }

  private async createScalarField(model: CatalogFieldComponent, field: CatalogQuestionModel) {
    let fieldSet = false;

    const fieldName = field.name;
    const fieldType = field.datatype;
    const fieldRenderer = field.renderer || model.datatypeToDefaultRenderer[fieldType];

    const properties: any = {
      fieldName: fieldName,
      fieldType: fieldType,
      renderer: fieldRenderer,
      rendererProperties: field.rendererProperties,
      required: model.isMandatory(field),
      readOnly: model.isReadonly(field),
      viewReadOnly: model.isReadonly(field),
      i18nKey: model.generateI18nKey(field),
      tooltipI18nKey: field.tooltip,
    };

    // For html fields we have to observe fewer events so we don't send so many requests to the server.
    if (fieldType == "html") {
      // Attachment target 'Catalog' allows us to get past Permissions issues for Catalog attachments.
      properties.rendererProperties = { "tinymce.events": "focus blur dirty", attachment_target: "CatalogQuestion" };
    }

    // Some renderers require the value to be a specific type.
    const convertValueTypeIfRequired = (fieldType: string, value: any) => {
      return (fieldType == 'acknowledgement' || fieldType == 'boolean') ? model.utilService.toBoolean(value) : value;
    }

    // If we want to preserve the answer we do it first, otherwise we follow the usual sequence.
    if (field.preserve && field.data.answer) {
      properties.initialValue = convertValueTypeIfRequired(fieldType, field.data.answer.value);
      fieldSet = true;
    } else if (field.data.defaultValueSource == "question") {
      properties.initialValue = field.data.defaultValue ? convertValueTypeIfRequired(fieldType, field.data.defaultValue.value) : null;
      fieldSet = true;
    } else if (["function", "script", "nlu"].includes(field.data.defaultValueSource) &&
      ((!field.alwaysUseDefault && (!field.data.answer || model.isReadonly(field))) || field.alwaysUseDefault)) {
      properties.initialValue = field.data.defaultValue ? convertValueTypeIfRequired(fieldType, field.data.defaultValue.value) : null;
      fieldSet = true;
    } else if (field.data.answer) {
      properties.initialValue = convertValueTypeIfRequired(fieldType, field.data.answer.value);
      fieldSet = true;
    }

    // Special case for boolean and switch renderer. The initial value will mean that the subscription will not fire and so the first event
    // is a real one. Hence, we need to set firstSettingOfSwitch to false.
    if (properties.initialValue !== undefined && properties.initialValue != null && fieldType == "boolean" && fieldRenderer == "switch") {
      model.firstSettingOfSwitch = false;
    }

    const bindingContext = (window as any).KO_FIELD_DEFINITION_BRIDGE.createStandaloneFieldDefinition(model, properties);

    bindingContext["data"][fieldName].value.subscribe((value) => {

      // The tinyMCE html editor will always set the value to empty string the first time.
      if (value == "" && model.firstSettingOfHtml == true && fieldType == "html") {
        model.firstSettingOfHtml = false;
        return;
      }

      // Switch renderer will always set the value to false the first time
      if (value == false && model.firstSettingOfSwitch == true && fieldType == "boolean" && fieldRenderer == "switch") {
        model.firstSettingOfSwitch = false;
        return;
      }

      if (!model.field.data.answer) {
        model.field.data.answer = {};
      }
      model.field.data.answer.value = value;
      // The displayValue will now be wrong so we have to remove it
      if ('displayValue' in model.field.data.answer) {
        delete model.field.data.answer.displayValue;
      }

      model.field.changed.emit(model.field);
    });

    // Force the update if a default value is supplied.
    if (fieldSet) {
      // bindingContext["data"][fieldName].value(bindingContext["data"][fieldName].value());
      // Doesn't work for scalar fields as KO ignores setting the same value that it already has.
      if (!model.field.data.answer) {
        model.field.data.answer = {};
      }
      model.field.data.answer.value = bindingContext["data"][fieldName].value();
      // The displayValue will now be wrong so we have to remove it
      if ('displayValue' in model.field.data.answer) {
        delete model.field.data.answer.displayValue;
      }
    }

    let result: any = {
      getAspect() {
        return "SelfService";
      },
      "$root": {
        formDisplayType: 'form',
      },
      fieldDefinition: bindingContext["fieldDefinition"],
      field: bindingContext["data"][fieldName],
      record: bindingContext["record"],
      data: {
        isNewRecord: () =>{
          return true;
        }
      }
    };

    if (fieldSet) {
      model.field.changedDefault.emit(model.field);
    }

    return result;
  }

  private generateVirtualTableName(field: CatalogQuestionModel): any {
    return `CatalogItem:${field.catalogItemId.split(":")[0]}`;
  }

  private generateI18nKey(field: CatalogQuestionModel): string {
    return field.question;
  }

  private async createReferenceField(model: CatalogFieldComponent, field: CatalogQuestionModel) {
    let fieldSet = false;

    const fieldName = field.name;
    const fieldType = field.datatype;
    const tableName = model.generateVirtualTableName(field);

    const isMulti = fieldType == 'many_to_many';

    const properties: any = {
      fieldName: fieldName,
      relationType: fieldType,
      table: tableName,
      renderer: field.referenceTable === "Attachment" ? "attachment" : model.datatypeToDefaultRenderer[fieldType],
      required: model.isMandatory(field),
      readOnly: model.isReadonly(field),
      viewReadOnly: model.isReadonly(field),
      i18nKey: model.generateI18nKey(field),
      tooltipI18nKey: field.tooltip,
      rendererProperties: {
        useReferenceQualifiers: true,
        enablePreview: field.enablePreview !== undefined ? field.enablePreview : true,
        attachmentSyncLoad: field.referenceTable === "Attachment" && isMulti, // For attachments only
        parallelUploads: 1, // For attachments only
      }
    };

    // Add the properties from the Catalog Item
    if (field.rendererProperties) {
      Object.entries(field.rendererProperties).forEach((kv) => {
          properties.rendererProperties[kv[0]] = kv[1];
      });
    }

    // TODO: what a mess, clean up the duplicated code
    // If we want to preserve the answer we do it first, otherwise we follow the usual sequence.
    if (field.preserve && field.data.answer) {
      if (isMulti) {
        if (field.data.answer.value) {
          let displayValues = field.data.answer.displayValue.split("|") // WARN: using | not ,
          let value = (field.data.answer.value as string).split(",").filter(v => v.length > 0).map((v, idx) => {
            return {
              id: v,
              value: {
                displayValue: displayValues[idx],
                readOnly: model.isReadonly(field),
                required: model.isMandatory(field),
                value: v
              },
              _tableClass: field.referenceTable
            }
          });

          properties.initialValue = {
            value: value
          };
        }
      } else {
        let value: any = {
          displayValue: field.data.answer.displayValue,
          readOnly: model.isReadonly(field),
          required: model.isMandatory(field),
          value: field.data.answer.value
        };

        properties.initialValue = {
          id: field.data.answer.value,
          value: value,
          _tableClass: field.referenceTable
        }
      }
      fieldSet = true;
    } else if (field.data.defaultValueSource == "question") {

      if (isMulti) {
        if (field.data?.defaultValue?.value) {
          let displayValues = field.data.defaultValue.displayValue.split("|") // WARN: using | not ,
          let value = (field.data.defaultValue.value as string).split(",").filter(v => v.length > 0).map((v, idx) => {
              return {
                id: v,
                value: {
                  displayValue: displayValues[idx],
                  readOnly: model.isReadonly(field),
                  required: model.isMandatory(field),
                  value: v
                },
                _tableClass: field.referenceTable
              }
          });

          properties.initialValue = {
            value: value
          }
        } else {
          properties.initialValue = null;
        }
      } else {
        if (field.data?.defaultValue?.value) {
          properties.initialValue = {
            id: field.data.defaultValue.value,
            value: {
              displayValue: field.data.defaultValue.displayValue,
              readOnly: model.isReadonly(field),
              required: model.isMandatory(field),
              value: field.data.defaultValue.value
            },
            _tableClass: field.referenceTable
          }
        } else {
          properties.initialValue = null;
        }
      }
      fieldSet = true;
    } else if ( ["function", "script", "nlu"].includes(field.data.defaultValueSource) &&
        field.data.defaultValue &&
        ((!field.alwaysUseDefault && (!field.data.answer || model.isReadonly(field))) || field.alwaysUseDefault)) {

      if (isMulti) {
        if (field.data?.defaultValue?.value) {
          let displayValues = field.data.defaultValue.displayValue.split("|") // WARN: using | not ,
          let value = (field.data.defaultValue.value as string).split(",").filter(v => v.length > 0).map((v, idx) => {
            return {
              id: v,
              value: {
                displayValue: displayValues[idx],
                readOnly: model.isReadonly(field),
                required: model.isMandatory(field),
                value: v
              },
              _tableClass: field.referenceTable
            }
          });

          properties.initialValue = {
            value: value
          }
        } else {
          properties.initialValue = null;
        }
      } else {
        if (field.data?.defaultValue?.value) {
          properties.initialValue = {
            id: field.data.defaultValue.value,
            value: {
              displayValue: field.data.defaultValue.displayValue,
              readOnly: model.isReadonly(field),
              required: model.isMandatory(field),
              value: field.data.defaultValue.value
            },
            _tableClass: field.referenceTable
          }
        } else {
          properties.initialValue = null;
        }
      }
      fieldSet = true;
    } else if (field.data.answer) {
      if (isMulti) {
        if (field.data.answer.value) {
          let displayValues = field.data.answer.displayValue.split("|") // WARN: using | not ,
          let value = (field.data.answer.value as string).split(",").filter(v => v.length > 0).map((v, idx) => {
            return {
              id: v,
              value: {
                displayValue: displayValues[idx],
                readOnly: model.isReadonly(field),
                required: model.isMandatory(field),
                value: v
              },
              _tableClass: field.referenceTable
            }
          });

          properties.initialValue = {
            value: value
          };
        }
      } else {
        let value: any = {
          displayValue: field.data.answer.displayValue,
          readOnly: model.isReadonly(field),
          required: model.isMandatory(field),
          value: field.data.answer.value
        };

        properties.initialValue = {
          id: field.data.answer.value,
          value: value,
          _tableClass: field.referenceTable
        }
      }
      fieldSet = true;
    }

    if (field.referenceTable === "Attachment") {
      if (properties.initialValue) {
        if (isMulti) {
          properties.initialValue.value.forEach((r) => {
            r.value.additionalValues = {MimeType: mime.getType(r.value.displayValue)};
          });
        } else {
          properties.initialValue.value.additionalValues = {MimeType: mime.getType(properties.initialValue.value.displayValue)};
        }
      }
    }

    const bindingContext = (window as any).KO_FIELD_DEFINITION_BRIDGE.createStandaloneReferenceFieldDefinition(model, properties);

    bindingContext["data"][fieldName].value.subscribe((value) => {
      // The value will be null if the reference field is cleared. It will be [] if a multi reference and cleared.
      if (field.referenceTable === "Attachment") {
        if (!model.field.data.answer) {
          model.field.data.answer = {};
        }

        if (isMulti) {
          model.field.data.answer.value = !value ? null : (Array.isArray(value) ? value.map(v => UtilService.dot(v, "id")).join(',') : value);
          model.field.data.answer.displayValue = !value ? null : (Array.isArray(value) ? value.map(v => UtilService.dot(v, "fileName")).join('|') : value);
        } else {
          model.field.data.answer.value = value ? value.id : null;
          model.field.data.answer.displayValue =  value ? value.fileName : null;
        }
      } else {
        // The value is an object containing the reference id
        if (!model.field.data.answer) {
          model.field.data.answer = {};
        }
        if (isMulti) {
          model.field.data.answer.value = !value ? null : (Array.isArray(value) ? value.map(v => UtilService.dot(v, "id")).join(',') : value);
          // The displayValue will now be wrong so we have to remove it
          if ('displayValue' in model.field.data.answer) {
            delete model.field.data.answer.displayValue;
          }
        } else {
          model.field.data.answer.value = value ? value.id : null;
          // The displayValue will now be wrong so we have to remove it
          if ('displayValue' in model.field.data.answer) {
            delete model.field.data.answer.displayValue;
          }
        }
      }

      model.field.changed.emit(model.field);
    });

    // Set validation errors
    if (model.field.data.validationMessage) {
      bindingContext["data"][fieldName].value.hasError(true);
      bindingContext["data"][fieldName].value.validationMessage(model.field.data.validationMessage);
    } else {
      bindingContext["data"][fieldName].value.hasError(false);
      bindingContext["data"][fieldName].value.validationMessage(null);
    }

    // The reference field doesn't update the value if you supply a default value so we force it.
    if (field.data?.defaultValue?.value && !model.field.data.validationMessage) {
      bindingContext["data"][fieldName].value(bindingContext["data"][fieldName].value());
    }

    // The attachment renderer needs a record, so we build one with minimum values to support it.
    let record = bindingContext["record"];
    if (!record && field.referenceTable === "Attachment") {
      record = {
        TableClass: {
          value: () => {
            return "CatalogQuestion"; // There is a special case in AttachmentManager.createAttachmentFromUpload that requires this value.
          }
        },
        id: () => {
          return null;  // There is a special case in AttachmentManager.createAttachmentFromUpload that requires this value.
        }
      }
    }

    let result: any = {
      getAspect() {
        return "SelfService";
      },
      "$root": {
        formDisplayType: 'form',
      },
      fieldDefinition: bindingContext["fieldDefinition"],
      field: bindingContext["data"][fieldName],
      record: record,
      data: {
        isNewRecord: () => {
          return true;
        },
      },
    };

    if (fieldSet) {
      model.field.changedDefault.emit(model.field);
    }

    return result;
  }

  private async createChoiceField(model: CatalogFieldComponent, field: CatalogQuestionModel) {
    let fieldSet = false;

    const fieldName = field.name;
    const fieldType = field.datatype;

    const properties: any = {
      fieldName: fieldName,
      fieldType: fieldType,
      renderer: field.renderer || model.datatypeToDefaultRenderer[fieldType],
      availableValues: field.availableValues,
      required: model.isMandatory(field),
      readOnly: model.isReadonly(field),
      viewReadOnly: model.isReadonly(field),
      i18nKey: model.generateI18nKey(field),
      tooltipI18nKey: field.tooltip,
      rendererProperties: {
        unsorted: true
      }
    };

    // If we want to preserve the answer we do it first, otherwise we follow the usual sequence.
    if (field.preserve && field.data.answer) {
      properties.initialValue = field.data.answer.value;
      fieldSet = true;
    } else if (field.data.defaultValueSource == "question") {
      properties.initialValue = field.data.defaultValue ? field.data.defaultValue.value : null;
      fieldSet = true;
    } else if (["function", "script", "nlu"].includes(field.data.defaultValueSource) &&
      ((!field.alwaysUseDefault && (!field.data.answer || model.isReadonly(field))) || field.alwaysUseDefault)) {
      properties.initialValue = field.data.defaultValue ? field.data.defaultValue.value : null;
      fieldSet = true;
    } else if (field.data.answer) {
      properties.initialValue = field.data.answer.value;
      fieldSet = true;
    }

    let bindingContext = (window as any).KO_FIELD_DEFINITION_BRIDGE.createStandaloneFieldDefinition(model, properties);
    let koField = bindingContext["data"][fieldName];

    koField.value.subscribe((value) => {
      // The select2 binding will always set null on the field if there is no initial value and also
      // when the field is cleared. We have to distinguish the two cases.
      if ((value == null && model.firstSettingOfChoice == false) || value != null) {
        if (!model.field.data.answer) {
          model.field.data.answer = {};
        }
        model.field.data.answer.value = value;
        // The displayValue will now be wrong so we have to remove it
        if ('displayValue' in model.field.data.answer) {
          delete model.field.data.answer.displayValue;
        }
        model.field.changed.emit(model.field);
      }
      model.firstSettingOfChoice = false;
    });

    // Force the update if a default value is supplied.
    if (fieldSet) {
      koField.value(koField.value());
    }

    let result: any = {
      getAspect() {
        return "SelfService";
      },
      "$root": {
        formDisplayType: 'form',
      },
      fieldDefinition: bindingContext["fieldDefinition"],
      field: koField,
      record: bindingContext["record"],
      data: {
        isNewRecord: () =>{
          return true;
        }
      }
    };

    if (fieldSet) {
      model.field.changedDefault.emit(model.field);
    }

    return result;
  }

  private isReadonly(field: CatalogQuestionModel) {
    const byAnswer = field?.data?.readonly;
    const byQuestion = field.readonly;
    return byAnswer != null ? byAnswer : byQuestion;
  }

  private isMandatory(field: CatalogQuestionModel) {
    const byAnswer = field?.data?.mandatory;
    const byQuestion = field.mandatory;
    return byAnswer != null ? byAnswer : byQuestion;
  }
}
